import { useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";

import { cn } from "@/lib/utils";

import { ensureAgGridRegistered, useCinchGridTheme } from "./agTheme";

// Defensive: guarantee the community modules are registered even if this panel
// is the first AG Grid import to execute.
ensureAgGridRegistered();

/** A column is either a bare field name (sensible defaults) or a full ColDef. */
export type GridColumn = string | ColDef;

/**
 * Reusable AG Grid panel (Community) with the Cinch brand + light/dark theme.
 * Pass `columns` as field names or ColDefs and `rows` as record objects. Sized
 * to fill its container; override with `className`.
 */
export function AgGridPanel({
  columns,
  rows,
  className,
  defaultColDef,
}: {
  columns: GridColumn[];
  rows: Record<string, unknown>[];
  className?: string;
  defaultColDef?: ColDef;
}) {
  const theme = useCinchGridTheme();
  const columnDefs = useMemo<ColDef[]>(
    () => columns.map((c) => (typeof c === "string" ? { field: c } : c)),
    [columns],
  );
  return (
    <div className={cn("h-[22rem] w-full", className)}>
      <AgGridReact
        theme={theme}
        columnDefs={columnDefs}
        rowData={rows}
        defaultColDef={{
          sortable: true,
          resizable: true,
          filter: true,
          flex: 1,
          minWidth: 110,
          ...defaultColDef,
        }}
        animateRows={false}
      />
    </div>
  );
}
